import { ChangeDetectionStrategy, Component, inject, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { LicenseService } from '@isophi/core-legacy';
import { BehaviorSubject, combineLatest, forkJoin, Observable, Subject, Subscription } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { KindergartenService } from '../../../core/services/kindergarten.service';
import { SubNav, SubnavService } from '../../../core/services/subnav.service';
import { MaterialService, SortType } from '../../dashboard/services/material.service';

@Component({
    selector: 'app-layout',
    templateUrl: './layout.component.html',
    styleUrls: ['./layout.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class LayoutComponent implements OnDestroy {
  private readonly kindergartenService = inject(KindergartenService);

  private readonly licenseService = inject(LicenseService);

  private readonly ngUnsubscribe$ = new Subject<void>();

  private hasShowcaseFeature = false;

  public showSubNav$: Observable<SubNav>;

  public showSubNavDemoKindergarten$ = new BehaviorSubject(false);

  private urlsWithSubNavResults: string[] = ['/child', '/reports', '/materials'];

  public urlWithoutBackground = true;

  private _subs: Subscription[] = [];

  constructor(private subNavService: SubnavService, private router: Router, protected materialService: MaterialService) {
    this.showSubNav$ = this.subNavService.showSubNav$;

    this.initMaterialData();

    // Get navigation events
    const navigationEnd$ = this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd)
    );

    // Combine both observables to handle demo kindergarten visibility
    combineLatest([navigationEnd$, this.licenseService.canSeeDemoKindergarten])
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe(([event, hasShowcase]) => {
        this.hasShowcaseFeature = hasShowcase;

        const splittedUrl = event.url.split('/');
        const url = '/' + splittedUrl[1];
        const urlMaterial = '/' + splittedUrl[2];

        if (
          this.urlsWithSubNavResults.includes(url) &&
          this.subNavService.showSubNavValue !== SubNav.RESULTS &&
          !event.url.includes('child/new')
        ) {
          this.showSubNavDemoKindergarten$.next(
            !this.kindergartenService.loggedKindergarten.isDemo && hasShowcase
          );
          this.subNavService.toggleSubNav(SubNav.RESULTS);
        } else if (this.urlsWithSubNavResults.includes(urlMaterial) && this.subNavService.showSubNavValue !== SubNav.MATERIALS) {
          this.showSubNavDemoKindergarten$.next(false);
          this.subNavService.toggleSubNav(SubNav.MATERIALS);
        } else {
          this.showSubNavDemoKindergarten$.next(false);
          this.subNavService.toggleSubNav(null);
        }

        this.urlWithoutBackground = event.url === '/dashboard';
      });
  }

  initMaterialData() {
    this._subs.push(
      forkJoin([
        this.materialService.getProducts(SortType.DIAGNOSTIC),
        this.materialService.getDownloadableMaterials(SortType.DIAGNOSTIC),
        this.materialService.getProducts(SortType.EDUCATIONAL),
        this.materialService.getDownloadableMaterials(SortType.EDUCATIONAL),
      ]).subscribe(([diagProducts, diagMaterials, eduProducts, eduMaterials]) => {
        this.materialService.setDiagnosticProductsSubject(diagProducts);
        this.materialService.setDiagnosticMaterialsSubject(diagMaterials);
        this.materialService.setEducationalProductsSubject(eduProducts);
        this.materialService.setEducationalMaterialsSubject(eduMaterials);
      })
    );
  }

  ngOnDestroy(): void {
    this._subs.forEach((_sub) => _sub.unsubscribe());
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }
}
